// ============================================================================
// SPECIALIST QUEUE HEALTH VIEW — Specialist-scoped operational dashboard
//
// Role access: specialist only (specialist_admin excluded at route level)
//
// Action Required (top row, 3 cards):
//   1. Today's Patients      — SCHEDULED with appointmentDate === today
//   2. Unreconciled Visits   — SCHEDULED with appointmentDate < today (fire when > 0)
//   3. Pending Consult Notes — SEEN without closedLoop.closedAt (72h fire)
//
// Clinical Volume (bottom row, 3 cards):
//   4. Upcoming Volume       — SCHEDULED with appointmentDate in next 14 days
//   5. Closed Loop Volume    — CLOSED_LOOP in last 30 days
//   6. No-Show Rate          — NO_SHOW / (SEEN + CLOSED_LOOP + NO_SHOW) last 30 days
//
// Scope: filtered to workflow.scheduling.consultantId === user.uid
//        AND referral.specialtyId ∈ user.specialtyIds
//
// Data source: listSpecialistQueueItems (SCHEDULED, SEEN, CLOSED_LOOP, NO_SHOW)
//
// Zero-PHI invariant: this view never reads from /phi/**.
// ============================================================================

import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../../auth/AuthContext.jsx';
import {
  listSpecialistQueueItems,
  getImpactSettings,
  DEFAULT_IMPACT_SETTINGS,
  WORKFLOW_STATUS,
  queueSlaStatus,
} from '../../core/index.js';
import {
  Activity, RefreshCw, AlertTriangle, Flame,
  Calendar, Clock, CheckCircle2, UserX, Stethoscope, AlertCircle,
} from 'lucide-react';

// ── Helpers ──────────────────────────────────────────────────────────────────

function toMillis(ts) {
  if (!ts) return null;
  if (ts.toMillis) return ts.toMillis();
  if (ts.seconds) return ts.seconds * 1000;
  if (ts instanceof Date) return ts.getTime();
  if (typeof ts === 'number') return ts;
  return null;
}

function toDateStr(ts) {
  if (!ts) return null;
  if (typeof ts === 'string') return ts.slice(0, 10);
  const ms = toMillis(ts);
  if (!ms) return null;
  return new Date(ms).toISOString().slice(0, 10);
}

function daysFromToday(dateStr, todayStr) {
  if (!dateStr) return Infinity;
  const d = new Date(dateStr + 'T00:00:00');
  const t = new Date(todayStr + 'T00:00:00');
  return Math.round((d - t) / (24 * 60 * 60 * 1000));
}

// ── Sub-components ──────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="bg-slate-800/30 border border-slate-700/20 rounded-2xl p-5 animate-pulse">
      <div className="h-3 w-24 bg-slate-700/60 rounded mb-3" />
      <div className="h-10 w-16 bg-slate-700/60 rounded" />
    </div>
  );
}

// Severity-driven styling. `severity` ('critical'|'warn'|'active'|'empty') comes
// from queueSlaStatus (share-of-queue) and overrides the legacy `fire` boolean.
// 'active' = breached count > 0 but below warn — still visibly shown, no fire color.
const METRIC_SEVERITY = {
  critical: { box: 'bg-red-500/10 border-red-500/20',     accent: 'text-red-400',   sub: 'text-red-400/70',   pulse: 'bg-red-500' },
  warn:     { box: 'bg-amber-500/10 border-amber-500/20', accent: 'text-amber-400', sub: 'text-amber-400/70', pulse: 'bg-amber-500' },
  active:   { box: 'bg-slate-800/50 border-slate-700/40', accent: null,             sub: 'text-slate-500',    pulse: null },
  empty:    { box: 'bg-slate-800/50 border-slate-700/40', accent: null,             sub: 'text-slate-500',    pulse: null },
};

function MetricCard({ label, value, subtitle, icon: Icon, colorTw, fire, fireLabel, onClick, severity }) {
  // severity overrides legacy fire boolean; fall back to fire→critical for callers that still pass it.
  const tier = severity || (fire === true ? 'critical' : 'empty');
  const sev = METRIC_SEVERITY[tier] || METRIC_SEVERITY.empty;
  const isAlert = tier === 'critical' || tier === 'warn';
  const accent = sev.accent || colorTw;
  const clickable = !!onClick;
  return (
    <div
      className={`relative overflow-hidden rounded-2xl border p-5 transition-all ${sev.box}
      ${clickable ? 'cursor-pointer hover:ring-1 hover:ring-slate-500/30' : ''}`}
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    >
      {isAlert && sev.pulse && (
        <div className={`absolute inset-0 ${sev.pulse} animate-pulse opacity-10 rounded-2xl`} />
      )}
      <div className="relative flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Icon className={`w-4 h-4 ${accent}`} />
            <p className={`text-xs font-medium ${isAlert ? accent : 'text-slate-400'}`}>
              {label}
            </p>
          </div>
          <p className={`text-4xl font-black tracking-tight ${accent}`}>
            {value}
          </p>
          {subtitle && (
            <p className={`text-xs mt-1 ${sev.sub}`}>
              {subtitle}
            </p>
          )}
          {isAlert && fireLabel && (
            <span className={`inline-flex items-center gap-1 mt-2 text-xs px-2 py-0.5 rounded-full font-medium ${
              tier === 'critical' ? 'bg-red-500/20 text-red-300' : 'bg-amber-500/20 text-amber-300'
            }`}>
              <Flame className="w-3 h-3" />{fireLabel}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function RateCard({ label, rate, numerator, denominator, icon: Icon, colorTw }) {
  return (
    <div className="bg-slate-800/50 border border-slate-700/40 rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${colorTw}`} />
        <p className="text-xs font-medium text-slate-400">{label}</p>
      </div>
      <p className={`text-4xl font-black tracking-tight ${colorTw}`}>
        {rate}
      </p>
      <p className="text-xs text-slate-500 mt-1">
        {numerator} of {denominator} completed referrals
      </p>
    </div>
  );
}

// ── Main View ────────────────────────────────────────────────────────────────

export default function SpecialistQueueHealthView({ onNavigate, activeFocusTarget, onLearningContext }) {
  const { user } = useContext(AuthContext);
  const tenantId = user?.tenantId;
  const uid = user?.uid;
  const specialtyIds = user?.specialtyIds ?? [];
  const specialtyIdsKey = specialtyIds.slice().sort().join(',');

  const [items, setItems] = useState(null);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (onLearningContext) onLearningContext({});
  }, [onLearningContext]);

  useEffect(() => {
    if (!activeFocusTarget) return;
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-learning-target="${activeFocusTarget}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
    return () => clearTimeout(timer);
  }, [activeFocusTarget]);

  const hl = (id) => activeFocusTarget === id ? 'ring-2 ring-emerald-400/20' : '';

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      const [data, impactSettings] = await Promise.all([
        listSpecialistQueueItems(tenantId),
        getImpactSettings(tenantId),
      ]);
      setItems(data);
      setSettings(impactSettings || DEFAULT_IMPACT_SETTINGS);
      setError(null);
    } catch (err) {
      console.error('[SpecialistQueueHealth] Failed to load:', err);
      setError('Failed to load queue data. Please retry.');
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setItems(null);
    await load();
    setRefreshing(false);
  };

  const loading = (items === null || settings === null) && !error;

  // ── Derive metrics from live data ──────────────────────────────────────────

  const metrics = useMemo(() => {
    if (!items || !settings) return null;

    const todayStr = new Date().toISOString().slice(0, 10);

    const myItems = items.filter(({ referral, workflow }) => {
      if (specialtyIds.length > 0 && !specialtyIds.includes(referral?.specialtyId)) return false;
      return workflow?.scheduling?.consultantId === uid;
    });

    let todayCount = 0;
    let unreconciledCount = 0;
    let pendingNotesCount = 0;
    let pendingNotesFireCount = 0;
    let upcomingCount = 0;
    let closedCount = 0;
    let noShowCount = 0;
    let seenCount = 0;

    const nowMs = Date.now();
    const states = settings?.sla?.states || {};
    const seenSlaMs = (states.SEEN?.slaHours ?? 72) * 60 * 60 * 1000;

    for (const { workflow } of myItems) {
      const status = workflow?.status;
      const apptDate = workflow?.scheduling?.appointmentDate;
      const apptDateStr = typeof apptDate === 'string' ? apptDate : toDateStr(apptDate);

      if (status === WORKFLOW_STATUS.SCHEDULED) {
        if (apptDateStr === todayStr) {
          todayCount++;
        } else {
          const days = daysFromToday(apptDateStr, todayStr);
          if (days < 0) {
            unreconciledCount++;
          } else if (days > 0 && days <= 14) {
            upcomingCount++;
          }
        }
      } else if (status === WORKFLOW_STATUS.SEEN) {
        seenCount++;
        pendingNotesCount++;
        const seenMs = toMillis(workflow?.scheduling?.seenAt);
        if (seenMs && (nowMs - seenMs) > seenSlaMs) {
          pendingNotesFireCount++;
        }
      } else if (status === WORKFLOW_STATUS.CLOSED_LOOP) {
        closedCount++;
      } else if (status === WORKFLOW_STATUS.NO_SHOW) {
        noShowCount++;
      }
    }

    const completedTotal = seenCount + closedCount + noShowCount;
    const noShowRate = completedTotal > 0
      ? Math.round((noShowCount / completedTotal) * 100)
      : 0;

    return {
      todayCount,
      unreconciledCount,
      pendingNotesCount,
      pendingNotesFire: pendingNotesFireCount > 0,
      pendingNotesFireCount,
      upcomingCount,
      closedCount,
      noShowCount,
      seenCount,
      noShowRate,
      completedTotal,
    };
  }, [items, settings, uid, specialtyIdsKey]);

  // ── Severity from tenant warn/critical thresholds (share-of-queue) ──────────
  // SLA timers (above) decide which items are stuck; queueSlaStatus decides color.
  // A breached count > 0 below warn renders as 'active' so work stays visible.
  const warnPct = settings?.sla?.thresholds?.warnPercent ?? DEFAULT_IMPACT_SETTINGS.sla.thresholds.warnPercent;
  const critPct = settings?.sla?.thresholds?.criticalPercent ?? DEFAULT_IMPACT_SETTINGS.sla.thresholds.criticalPercent;
  const sevTier = (workCount, stuckCount, queueCount) => {
    if (!workCount) return 'empty';
    const s = queueSlaStatus(stuckCount, queueCount, warnPct, critPct);
    return s === 'critical' ? 'critical' : s === 'warn' ? 'warn' : 'active';
  };
  const scheduledQueue = metrics ? (metrics.todayCount + metrics.upcomingCount + metrics.unreconciledCount) : 0;
  const unreconciledSeverity = metrics ? sevTier(metrics.unreconciledCount, metrics.unreconciledCount, scheduledQueue) : 'empty';
  const pendingNotesSeverity = metrics ? sevTier(metrics.pendingNotesCount, metrics.pendingNotesFireCount, metrics.pendingNotesCount) : 'empty';

  // ── Render ──────────────────────────────────────────────────────────────────

  const todayStr = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-8">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className={`flex items-center justify-between rounded-xl transition-all ${hl('queue-health-header')}`} data-learning-target="queue-health-header">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal-500/20 flex items-center justify-center">
            <Activity className="w-5 h-5 text-teal-400" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-white">Queue Health</h1>
            <p className="text-xs text-slate-400">
              Specialist — Live — {todayStr}
              {metrics && (
                <span className="ml-2 text-slate-500">
                  · {metrics.todayCount} patient{metrics.todayCount !== 1 ? 's' : ''} today
                </span>
              )}
            </p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing || loading}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-800/60 hover:bg-slate-700/60
                     text-slate-400 hover:text-white text-xs font-medium disabled:opacity-50 transition-all"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* ── Error state ────────────────────────────────────────────────────── */}
      {error && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-500/10 border border-red-500/30
                        rounded-xl text-red-400 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button
            onClick={handleRefresh}
            className="text-xs underline underline-offset-2 hover:text-red-300"
          >
            Retry
          </button>
        </div>
      )}

      {/* ── Action Required ──────────────────────────────────────────────── */}
      {loading ? (
        <div className="space-y-6">
          <div>
            <div className="h-4 w-32 bg-slate-700/40 rounded mb-3 animate-pulse" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          </div>
          <div>
            <div className="h-4 w-28 bg-slate-700/40 rounded mb-3 animate-pulse" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          </div>
        </div>
      ) : metrics && (
        <div className="space-y-6">
          <div className={`rounded-xl transition-all ${hl('queue-health-live-fires')}`} data-learning-target="queue-health-live-fires">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
              Action Required
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className={`rounded-2xl transition-all ${hl('queue-health-todays-patients')}`} data-learning-target="queue-health-todays-patients">
                <MetricCard
                  label="Today's Patients"
                  value={metrics.todayCount}
                  subtitle={`Scheduled for ${todayStr}`}
                  icon={Calendar}
                  colorTw="text-teal-400"
                />
              </div>
              <div className={`rounded-2xl transition-all ${hl('queue-health-unreconciled-visits')}`} data-learning-target="queue-health-unreconciled-visits">
                <MetricCard
                  label="Unreconciled Visits"
                  value={metrics.unreconciledCount}
                  subtitle="Past appointments still SCHEDULED"
                  icon={AlertCircle}
                  colorTw="text-amber-400"
                  severity={unreconciledSeverity}
                  fireLabel={`${metrics.unreconciledCount} past due`}
                  onClick={metrics.unreconciledCount > 0 && onNavigate
                    ? () => onNavigate('specialist-worklist', { tab: 'needs_action' })
                    : undefined}
                />
              </div>
              <div className={`rounded-2xl transition-all ${hl('queue-health-pending-consult-notes')}`} data-learning-target="queue-health-pending-consult-notes">
                <MetricCard
                  label="Pending Consult Notes"
                  value={metrics.pendingNotesCount}
                  subtitle="Seen but not yet closed"
                  icon={Stethoscope}
                  colorTw="text-purple-400"
                  severity={pendingNotesSeverity}
                  fireLabel={`${metrics.pendingNotesFireCount} past SLA`}
                  onClick={metrics.pendingNotesCount > 0 && onNavigate
                    ? () => onNavigate('specialist-worklist', { tab: 'needs_action' })
                    : undefined}
                />
              </div>
            </div>
          </div>

          {/* ── Clinical Volume ─────────────────────────────────────────────── */}
          <div className={`rounded-xl transition-all ${hl('queue-health-total-volume')}`} data-learning-target="queue-health-total-volume">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
              Clinical Volume
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <MetricCard
                label="Upcoming Volume"
                value={metrics.upcomingCount}
                subtitle="Next 14 days"
                icon={Clock}
                colorTw="text-blue-400"
              />
              <MetricCard
                label="Closed Loop Volume"
                value={metrics.closedCount}
                subtitle="Last 30 days"
                icon={CheckCircle2}
                colorTw="text-emerald-400"
              />
              <RateCard
                label="No-Show Rate"
                rate={`${metrics.noShowRate}%`}
                numerator={metrics.noShowCount}
                denominator={metrics.completedTotal}
                icon={UserX}
                colorTw={metrics.noShowRate > 15 ? 'text-amber-400' : 'text-slate-400'}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Empty state ────────────────────────────────────────────────────── */}
      {metrics && metrics.todayCount === 0 && metrics.unreconciledCount === 0 &&
       metrics.pendingNotesCount === 0 && metrics.upcomingCount === 0 &&
       metrics.closedCount === 0 && metrics.noShowCount === 0 && (
        <div className="px-4 py-3 bg-slate-800/30 border border-slate-700/20
                        rounded-xl text-slate-500 text-sm text-center">
          No referral activity found for your specialist profile.
        </div>
      )}

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <p className={`text-xs text-slate-600 text-center rounded-xl transition-all ${hl('queue-health-act-from-signal')}`} data-learning-target="queue-health-act-from-signal">
        Live data from workflow states · Scoped to your specialist assignments · SLA-based fire threshold on pending notes
      </p>
    </div>
  );
}
